import { AfterViewInit, Component, ElementRef, HostListener, Inject, NgZone, ViewChild } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { PusherService } from './services/pusher.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './services/auth.service';
import { filter } from 'rxjs/operators';
import { UserService } from './services/user.service';
import { ModalController } from '@ionic/angular';

import { DealerSelectorComponent } from './components/dealer-selector/dealer-selector.component';
import { AlertComponent } from './components/alerts/alerts.component';
import { Title } from '@angular/platform-browser';
import { ContextMenuComponent } from './components/conversations/context-menu/context-menu.component';
import { environment } from 'src/environments/environment';
import { DOCUMENT } from '@angular/common';
import { CallPopupService } from './services/call-popup.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ThemeService } from './services/theme.service';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { NativeNotificationService } from './services/native-notification.service';
import { PushTokenService } from './services/push-token.service';
import { VoicemailService } from './services/voicemail.service';
import { NativeTwilioVoiceService } from './services/native-twilio-voice.service';
import { ConversationService } from './services/conversation.service';
import { LeadService } from './services/leads.service';

const currentVersion = "4.0.0";
const LEAD_NOTIFICATION_ROLE_IDS = [2, 3];

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
})
export class AppComponent implements AfterViewInit {
    
    @ViewChild(ContextMenuComponent) contextMenu: ContextMenuComponent;
    @ViewChild('pullRefreshIndicator') pullRefreshIndicator: ElementRef<HTMLElement>;
    @ViewChild('pullRefreshIcon') pullRefreshIcon: ElementRef<SVGSVGElement>;
    @ViewChild('pullRefreshArrowPath') pullRefreshArrowPath: ElementRef<SVGPathElement>;
    @ViewChild('pullRefreshArrowHead') pullRefreshArrowHead: ElementRef<SVGPathElement>;
    user: any;
    isReady: boolean = false;
    uuid:any = '';
    show: any = { profile: false };
    isMenuOpen: boolean = false;
    routeName: string = '';
    SubName: string='';
    isCallPopupRoute: boolean = false;
    incomingLeadCount: number = 0;
    pullRefreshActive = false;
    pullReady = false;
    pullRefreshing = false;
    pullDistance = 0;
    pullRotation = 0;
    private pullStartX = 0;
    private pullStartY = 0;
    private pulling = false;
    private pullThresholdHapticPlayed = false;
    private readonly pullRefreshThreshold = 92;
    private readonly pullRestDistance = 104;
    private readonly maxPullDistance = 132;
    private pullAnimationFrame: number | null = null;
    private pendingPullDistance = 0;
    private pendingPullRotation = 0;
    private pendingPullProgress = 0;
    private removePullRefreshListeners: Array<() => void> = [];
    private startupSideEffectsDone = false;
    private lastLeadRefreshAt = 0;
    private leadRefreshInFlight = false;
    // private hasPrimedCallPopup = false;
    
    // Alerts
    _showAlertBanner: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    _showAlertBannerText: BehaviorSubject<string> = new BehaviorSubject<string>('');
    _showAlertUnread: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // Alert Observables
    get showAlertBanner(): Observable<boolean> {
        return this._showAlertBanner.asObservable();
    }

    get showAlertBannerText(): Observable<string> {
        return this._showAlertBannerText.asObservable();
    }
    private isMessageBound: boolean = false;

    constructor(
        private router: Router, 
        private route: ActivatedRoute,
        private authService: AuthService,
        public userService: UserService,
        private pusher: PusherService,
        private modalCtrl: ModalController,
        private titleService: Title,
        @Inject(DOCUMENT) private document: Document,
        private callPopupService: CallPopupService,
        private themeService: ThemeService,
        private nativeNotifications: NativeNotificationService,
        private pushTokenService: PushTokenService,
        public voicemailService: VoicemailService,
        private nativeTwilioVoice: NativeTwilioVoiceService,
        private conversationService: ConversationService,
        private leadService: LeadService,
        private ngZone: NgZone
    ) {
        this.themeService.initTheme();
        this.applyNativePlatformBodyClasses();
        this.registerAppUrlHandlers();
        this.nativeNotifications.init();
        this.pushTokenService.init();
        this.getAuthenticatedUser();
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.setRouteNameAndSubName();
            this.isCallPopupRoute = this.router.url.startsWith('/call-popup');
            this.toggleCallPopupBodyClass(this.isCallPopupRoute);
        });
        this.authService.loginSuccess$.subscribe(() => {
            this.refreshIncomingLeadCount();
        });
        this.leadService.leadsChanged$.subscribe(() => {
            this.refreshIncomingLeadCount();
        });
    }
    
    private applyNativePlatformBodyClasses(): void {
        const body = this.document?.body;
        if (!body || !Capacitor.isNativePlatform()) return;

        body.classList.add('native-app');

        if (Capacitor.getPlatform() === 'android') {
            body.classList.add('native-android');
        } else if (Capacitor.getPlatform() === 'ios') {
            body.classList.add('native-ios');
        }
    }

    ngOnInit(): void {
        this.setConversationSubscription(); // Subscribe to conversation updates
    }

    ngAfterViewInit(): void {
        this.ngZone.runOutsideAngular(() => {
            const touchStart = (event: TouchEvent) => this.onDocumentTouchStart(event);
            const touchMove = (event: TouchEvent) => this.onDocumentTouchMove(event);
            const touchEnd = () => this.onDocumentTouchEnd();

            document.addEventListener('touchstart', touchStart, { passive: true });
            document.addEventListener('touchmove', touchMove, { passive: false });
            document.addEventListener('touchend', touchEnd, { passive: true });
            document.addEventListener('touchcancel', touchEnd, { passive: true });

            this.removePullRefreshListeners = [
                () => document.removeEventListener('touchstart', touchStart),
                () => document.removeEventListener('touchmove', touchMove),
                () => document.removeEventListener('touchend', touchEnd),
                () => document.removeEventListener('touchcancel', touchEnd),
            ];
        });
    }

    @HostListener('window:resize')
    onResize(): void {
        if (!this.isPullRefreshViewport()) {
            this.resetPullRefresh();
        }
    }

    onDocumentTouchStart(event: TouchEvent): void {
        if (!this.canStartPullRefresh(event)) {
            return;
        }

        this.pullStartX = event.touches[0].clientX;
        this.pullStartY = event.touches[0].clientY;
        this.pulling = true;
        this.pullReady = false;
        this.pullDistance = 0;
        this.pullRotation = 0;
        this.pullRefreshActive = false;
        this.pullThresholdHapticPlayed = false;
        this.setPullRefreshVisual(0, 0, false, false, false);
    }

    onDocumentTouchMove(event: TouchEvent): void {
        if (!this.pulling || this.pullRefreshing || event.touches.length !== 1) {
            return;
        }

        if (!this.isPullRefreshViewport() || !this.isPageAtTop(event.target as HTMLElement | null, 3)) {
            this.resetPullRefresh();
            return;
        }

        const deltaX = event.touches[0].clientX - this.pullStartX;
        const deltaY = event.touches[0].clientY - this.pullStartY;

        if (Math.abs(deltaX) > Math.max(35, deltaY * 0.8)) {
            this.resetPullRefresh();
            return;
        }

        if (deltaY <= 0) {
            this.pullDistance = 0;
            this.pullRotation = 0;
            this.pullRefreshActive = false;
            this.pullReady = false;
            this.setPullRefreshVisual(0, 0, false, false, false);
            return;
        }

        if (event.cancelable && deltaY > 8) {
            event.preventDefault();
        }

        this.pullDistance = this.getPullDisplayDistance(deltaY);
        this.pullRotation = Math.min(405, (this.pullDistance / this.pullRefreshThreshold) * 360);
        this.pullRefreshActive = this.pullDistance > 0;
        this.pullReady = this.pullDistance >= this.pullRefreshThreshold;

        if (!this.pullReady && this.pullDistance < this.pullRefreshThreshold - 12) {
            this.pullThresholdHapticPlayed = false;
        }

        if (this.pullReady && !this.pullThresholdHapticPlayed) {
            this.pullThresholdHapticPlayed = true;
            this.playPullRefreshHaptic();
        }

        this.setPullRefreshVisual(this.pullDistance, this.pullRotation, this.pullRefreshActive, this.pullReady, false);
    }

    onDocumentTouchEnd(): void {
        if (!this.pulling) {
            return;
        }

        this.pulling = false;

        if (this.pullReady) {
            this.triggerPageRefresh();
            return;
        }

        this.resetPullRefresh();
    }

    private canStartPullRefresh(event: TouchEvent): boolean {
        return this.isReady
            && !this.isCallPopupRoute
            && !this.pullRefreshing
            && event.touches.length === 1
            && this.isPullRefreshViewport()
            && this.isPageAtTop(event.target as HTMLElement | null, 3);
    }

    private isPullRefreshViewport(): boolean {
        return window.innerWidth <= 1024 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    }

    private isPageAtTop(target: HTMLElement | null, tolerance = 0): boolean {
        if (
            window.scrollY > tolerance ||
            this.document.documentElement.scrollTop > tolerance ||
            this.document.body.scrollTop > tolerance
        ) {
            return false;
        }

        const ionContent = this.findRelevantIonContent(target);
        const ionScrollElement = ionContent?.shadowRoot?.querySelector('.inner-scroll') as HTMLElement | null;
        if (ionScrollElement && ionScrollElement.scrollTop > tolerance) {
            return false;
        }

        let current: HTMLElement | null = target;

        while (current && current !== this.document.body && current !== this.document.documentElement) {
            const style = window.getComputedStyle(current);
            const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY);

            if (canScrollY && current.scrollHeight > current.clientHeight && current.scrollTop > tolerance) {
                return false;
            }

            current = current.parentElement;
        }

        return true;
    }

    private findRelevantIonContent(target: HTMLElement | null): HTMLElement | null {
        const touchedIonContent = target?.closest?.('ion-content') as HTMLElement | null;
        if (touchedIonContent) {
            return touchedIonContent;
        }

        const visiblePageContent = Array.from(this.document.querySelectorAll('ion-router-outlet .ion-page:not(.ion-page-hidden) ion-content'))
            .find((content) => this.isVisibleElement(content as HTMLElement)) as HTMLElement | undefined;

        return visiblePageContent || null;
    }

    private isVisibleElement(element: HTMLElement): boolean {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
    }

    private getPullDisplayDistance(deltaY: number): number {
        if (deltaY <= 0) {
            return 0;
        }

        const revealDistance = Math.min(deltaY * 0.74, this.pullRestDistance);

        if (revealDistance < this.pullRestDistance) {
            return Math.max(4, revealDistance);
        }

        const extraPull = Math.max(0, deltaY - this.pullRestDistance / 0.74);
        const settleDistance = (this.maxPullDistance - this.pullRestDistance) * (1 - Math.exp(-extraPull / 170));

        return Math.min(this.maxPullDistance, this.pullRestDistance + settleDistance);
    }

    private playPullRefreshHaptic(): void {
        const navWithVibrate = navigator as Navigator & { vibrate?: (pattern: number | number[]) => boolean };

        try {
            navWithVibrate.vibrate?.(12);
        } catch (error) {
            // Haptics are best-effort and may be unavailable on some browsers/devices.
        }

        if (Capacitor.isNativePlatform()) {
            Haptics.impact({ style: ImpactStyle.Light }).catch(() => undefined);
        }
    }

    private setPullRefreshVisual(
        distance: number,
        rotation: number,
        active: boolean,
        ready: boolean,
        refreshing: boolean
    ): void {
        const indicator = this.pullRefreshIndicator?.nativeElement;
        if (!indicator) {
            return;
        }

        indicator.classList.toggle('visible', active || refreshing);
        indicator.classList.toggle('ready', ready && !refreshing);
        indicator.classList.toggle('refreshing', refreshing);

        this.pendingPullDistance = distance;
        this.pendingPullRotation = rotation;
        this.pendingPullProgress = refreshing
            ? 1
            : Math.min(1, Math.max(0, distance / this.pullRefreshThreshold));

        if (this.pullAnimationFrame !== null) {
            return;
        }

        this.pullAnimationFrame = window.requestAnimationFrame(() => {
            this.pullAnimationFrame = null;

            const nextDistance = this.pendingPullDistance;
            const nextRotation = this.pendingPullRotation;
            const nextProgress = this.pendingPullProgress;

            indicator.style.transform = `translate3d(-50%, ${nextDistance - 44}px, 0)`;

            const icon = this.pullRefreshIcon?.nativeElement;
            if (icon) {
                icon.style.transform = `rotate(${nextRotation}deg)`;
            }

            const arrowPath = this.pullRefreshArrowPath?.nativeElement;
            if (arrowPath) {
                arrowPath.style.strokeDashoffset = `${100 * (1 - nextProgress)}`;
            }

            const arrowHead = this.pullRefreshArrowHead?.nativeElement;
            if (arrowHead) {
                const headProgress = Math.max(0, Math.min(1, (nextProgress - 0.72) / 0.28));
                arrowHead.style.opacity = `${headProgress}`;
            }
        });
    }

    private triggerPageRefresh(): void {
        this.ngZone.run(() => {
            this.pullRefreshing = true;
        });
        this.pullRefreshActive = true;
        this.pullReady = false;
        this.pullDistance = 64;
        this.pullRotation = 360;
        this.setPullRefreshVisual(this.pullRestDistance, this.pullRotation, true, false, true);

        window.setTimeout(() => {
            window.location.reload();
        }, 120);
    }

    private resetPullRefresh(): void {
        this.pulling = false;
        this.pullRefreshActive = false;
        this.pullReady = false;
        this.pullRefreshing = false;
        this.pullDistance = 0;
        this.pullRotation = 0;
        this.pullThresholdHapticPlayed = false;
        this.setPullRefreshVisual(0, 0, false, false, false);
    }

    private registerAppUrlHandlers(): void {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        CapacitorApp.addListener('appUrlOpen', ({ url }) => {
            this.handleNativeAppUrl(url);
        });

        CapacitorApp.getLaunchUrl()
            .then((launchData) => {
                if (launchData?.url) {
                    this.handleNativeAppUrl(launchData.url);
                }
            })
            .catch((error) => {
                console.error('Failed to read launch URL:', error);
            });
    }

    private handleNativeAppUrl(url: string): void {
        if (!url) {
            return;
        }

        try {
            const parsed = new URL(url);
            const isCallUrl =
                parsed.protocol === 'advantageconnect:' &&
                (parsed.hostname === 'call' || parsed.pathname === '/call');

            if (!isCallUrl) {
                return;
            }

            const conversationId = Number(parsed.searchParams.get('conversationId') || '');
            const action = (parsed.searchParams.get('action') || '').toLowerCase();
            const callSid = parsed.searchParams.get('callSid') || '';

            if (Number.isFinite(conversationId) && conversationId > 0) {
                this.router.navigate(['conversations'], {
                    queryParams: { id: conversationId },
                }).catch((error) => {
                    console.error('Failed to route incoming call deep link:', error);
                });
            } else if (callSid) {
                this.conversationService.getCall(callSid).subscribe({
                    next: (call) => {
                        const targetConversationId = Number(call?.conversation_id || '');
                        const extras = Number.isFinite(targetConversationId) && targetConversationId > 0
                            ? { queryParams: { id: targetConversationId } }
                            : undefined;

                        this.router.navigate(['conversations'], extras).catch((error) => {
                            console.error('Failed to route incoming call by call SID:', error);
                        });
                    },
                    error: (error) => {
                        console.error('Failed to look up incoming call conversation:', error);
                        this.router.navigate(['conversations']).catch((navError) => {
                            console.error('Failed to route incoming call fallback:', navError);
                        });
                    },
                });
            }

            if (action === 'answer') {
                if (this.nativeTwilioVoice.isNativeAndroid()) {
                    this.nativeTwilioVoice.markAnswerRequested({
                        callSid,
                        conversationId: Number.isFinite(conversationId) && conversationId > 0
                            ? conversationId
                            : null,
                    });
                } else {
                    this.callPopupService.requestAction('answer');
                }
            }
        } catch (error) {
            console.error('Failed to parse native app URL:', error);
        }
    }

    // @HostListener('document:click')
    // @HostListener('document:keydown')
    // handleUserGestureForPopup(): void {
    //     if (this.hasPrimedCallPopup) return;
    //     const opened = this.callPopupService.primePopupWindow();
    //     this.hasPrimedCallPopup = opened;
    // }

    setConversationSubscription(): void {
        this.pusher.subscribe('multi_channel_notification');

        this.pusher.channel.bind('message', (data: any) => {
            if (data.conversation) {
                const conversationData = data.conversation.data;
                if (conversationData.store === this.userService.dealer.id) {
                    if (this.isVisibleTab()) {
                        this._showAlertBanner.next(true);
                        this._showAlertBannerText.next(conversationData.message || conversationData.body || '');
                        this.notifyNativeConversation(conversationData);

                        setTimeout(() => {
                            this._showAlertBanner.next(false);
                            this._showAlertBannerText.next('');
                        }, 4000);
                    }
                }
            }
            if (data.leads){
                const leadsData = data.leads.data;
                const leadsText= 'New Leads Received: ' + leadsData.total || '';
                if (leadsData.store === this.userService.dealer.id && this.hasAnyRole(LEAD_NOTIFICATION_ROLE_IDS)) {
                    if (this.isVisibleTab()) {
                        this._showAlertBanner.next(true);
                        this._showAlertBannerText.next(leadsText);
                        this.notifyNativeleads(leadsData, leadsData.total);

                        setTimeout(() => {
                            this._showAlertBanner.next(false);
                            this._showAlertBannerText.next('');
                        }, 4000);
                    }

                    this.refreshIncomingLeadCount();
            }
        }

            if (data.call) {
                const callPayload = data.call.data || data.call;
                const currentUserId = this.userService?.user?.id || this.user?.id || null;
                const currentDealerId = this.userService?.dealer?.id || this.user?.dealer?.id || null;
                const matchesAssignee = !callPayload.user_id || currentUserId === callPayload.user_id;
                const matchesDealer = !callPayload.dealer_id || currentDealerId === callPayload.dealer_id;

                if (matchesAssignee && matchesDealer && this.isVisibleTab() && !this.nativeTwilioVoice.isNativeAndroid()) {
                    this.nativeNotifications.notifyIncomingCall(callPayload);
                }
            }

            if (data.voicemail) {
                const voicemailPayload = data.voicemail.data || data.voicemail;
                const currentUserId = this.userService?.user?.id || this.user?.id || null;
                const currentDealerId = this.userService?.dealer?.id || this.user?.dealer?.id || null;
                const isAdmin = this.userService?.admin === 1;
                const matchesAssignee = isAdmin || !voicemailPayload.user_id || currentUserId === voicemailPayload.user_id;
                const matchesDealer = !voicemailPayload.dealer_id || currentDealerId === voicemailPayload.dealer_id;

                if (matchesAssignee && matchesDealer) {
                    this.voicemailService.refreshUnreadCount();
                }
            }
        });
    }

    setRouteNameAndSubName(): void {
        let child = this.route.firstChild;
        while (child) {
            if (child.snapshot.data['title']) {
                this.routeName = child.snapshot.data['title'];
                this.SubName = child.snapshot.data['subTitle'];
                break;
            }
            child = child.firstChild;
        }
    }

    private toggleCallPopupBodyClass(enable: boolean): void {
        const body = this.document?.body;
        if (!body) return;

        if (enable) {
            body.classList.add('call-popup-body');
        } else {
            body.classList.remove('call-popup-body');
        }
    }

    getQueryParams() {
        this.uuid = this.route
    }

    private forceRelogin(): void {
        localStorage.removeItem('user');
        this.userService.user = {};
        this.user = null;
        this.isReady = true;

        if (this.router.url !== '/login') {
            this.router.navigate(['login']);
        }
    }

    async getAuthenticatedUser() {
        try {
            const redirectParams = new URLSearchParams(window.location.search);
            if (
                window.location.pathname.endsWith('/redirect') &&
                redirectParams.has('auth') &&
                redirectParams.has('uuid')
            ) {
                this.isReady = true;
                return;
            }

            // console.log(environment.host);
            // console.log(environment.api_url);
            // Check if the stored version matches the current version
            const storedVersion = localStorage.getItem('app_version');
            if (storedVersion !== currentVersion) {
                localStorage.clear();
                localStorage.setItem('app_version', currentVersion);
            }
    
            // Fetch the user data from localStorage
            const rawUser = localStorage.getItem('user');
            const user = rawUser ? JSON.parse(rawUser) : null;
            const localUserToken = user?.user?.token;

            if (user && localUserToken) {
                // console.log("get authenticated user loaded");
                const response = await this.userService.getUserDetails(localUserToken).toPromise();
                const backendToken = response?.user?.token;

                // Detect localStorage token tampering/stale token and force a clean login.
                if (!backendToken || backendToken !== localUserToken) {
                    this.forceRelogin();
                    return;
                }

                localStorage.setItem('user', JSON.stringify(response));
                this.userService.setUserAccount(response);
                this.user = this.userService;
                this.isReady = true;
                this.runStartupSideEffects();
    
                // Subscribe to alerts
                this.setAlertSubscription();
    
                // Set User Alerts
                const alertCount = this.user.alertCount || 0;
                const dealerName = this.user.dealer?.name || '';
                this.titleService.setTitle(`${dealerName}${alertCount > 0 ? ` (${alertCount})` : ''} | ${this.titleService.getTitle()}`);
            } else {
                this.forceRelogin();
            }
        } catch (error) {
            console.error('Error fetching authenticated user:', error);
            const status = (error as HttpErrorResponse)?.status;
            if (status === 400 || status === 401 || status === 403) {
                this.forceRelogin();
                return;
            }
            this.isReady = true;
            this.voicemailService.refreshUnreadCount();
            this.refreshIncomingLeadCount();
        }
    }

    refreshIncomingLeadCount(): void {
        if (!this.userService?.dealer?.id) {
            this.incomingLeadCount = 0;
            return;
        }

        const now = Date.now();
        if (this.leadRefreshInFlight || now - this.lastLeadRefreshAt < 30000) {
            return;
        }

        this.leadRefreshInFlight = true;
        this.lastLeadRefreshAt = now;

        this.leadService.getLeads({ page: 1, sortBy: 'received_at', sortDir: 'desc' }).subscribe({
            next: (resp) => {
                this.incomingLeadCount = Number(resp['total'] || 0);
                this.leadRefreshInFlight = false;
            },
            error: () => {
                this.incomingLeadCount = 0;
                this.leadRefreshInFlight = false;
            },
        });
    }

    private runStartupSideEffects(): void {
        if (this.startupSideEffectsDone) {
            return;
        }

        this.startupSideEffectsDone = true;
        this.nativeNotifications.requestStartupPermissions();
        this.pushTokenService.registerCurrentDevice();
        this.nativeTwilioVoice.syncRegistration();
        this.voicemailService.refreshUnreadCount();
        this.refreshIncomingLeadCount();
    }

    isActivityCallRoute(): boolean {
        return this.router.url.startsWith('/activities')
            || this.router.url.startsWith('/call-logs')
            || this.router.url.startsWith('/voicemail-logs');
    }

    isActivitiesRoute(): boolean {
        return this.router.url.startsWith('/activities');
    }

    isCallLogsRoute(): boolean {
        return this.router.url.startsWith('/call-logs');
    }

    isVoicemailRoute(): boolean {
        return this.router.url.startsWith('/voicemail-logs');
    }
    
    /**
     * [setAlertSubscription - Subscribe to pusher socket]
     * @returns {void}
     */
    setAlertSubscription(): void {
        if (this.isMessageBound) {
            // console.log("Message listener already bound, skipping...");
            return;
        }
        this.isMessageBound = true;
    
        const userChannel = this.user.admin === 1
            ? `alert.ADMIN${this.userService.dealer.id}`
            : `alert.ADV${this.user.id}`;
        
        this.pusher.subscribe('multi_channel_notification');

        //console.log("Binding message listener");
        this.pusher.channel.bind('message', (data: any) => {
            const channelData = this.user.admin === 1 ? data.admin_channel : data.alert_channel;
    
            if (channelData && channelData.channel === userChannel && channelData.data.store === this.userService.dealer.id) {
                this.handleAlert(channelData.data);
            }
        });
    }
    

    ngOnDestroy(): void {
        this.removePullRefreshListeners.forEach((removeListener) => removeListener());
        this.removePullRefreshListeners = [];

        if (this.pullAnimationFrame !== null) {
            window.cancelAnimationFrame(this.pullAnimationFrame);
            this.pullAnimationFrame = null;
        }

        if (this.pusher?.channel) {
            //console.log("Unbinding message listener");
            // this.pusher.channel.unbind('message');
            // this.isMessageBound = false;
        }
    }
    
    handleAlert(alertData: any): void {
        const message = typeof alertData === 'string' ? alertData : alertData?.message || '';
        if (this.isVisibleTab()) {
            this._showAlertBanner.next(true);
            this._showAlertBannerText.next(message);
            this.notifyNativeAlert(alertData);
        }

        this.incrementAlertCounts(alertData);
        
        setTimeout(() => {
            this._showAlertBanner.next(false);
            this._showAlertBannerText.next('');
        }, 4000);
    }

    private isVisibleTab(): boolean {
        return this.document.visibilityState === 'visible';
    }

    private incrementAlertCounts(alertData: any): void {
        const store = Number(alertData?.store || 0);
        const currentDealerId = Number(this.userService?.dealer?.id || 0);

        if (store && currentDealerId && store !== currentDealerId) {
            this.userService.storeAlertCount = Number(this.userService.storeAlertCount || 0) + 1;
        } else {
            this.userService.alertCount = Number(this.userService.alertCount || 0) + 1;
        }

        this.user = this.userService;
        this.updateDocumentTitle();
    }

    private updateDocumentTitle(): void {
        const dealerName = this.userService.dealer?.name || '';
        const alertCount = Number(this.userService.alertCount || 0);
        const baseTitle = this.titleService.getTitle().replace(/^\(.+?\)\s*/, '').replace(/^.*?\|\s*/, '');
        this.titleService.setTitle(`${dealerName}${alertCount > 0 ? ` (${alertCount})` : ''} | ${baseTitle}`);
    }

    async onDealerSelectorClick() {
        try {
            const modal = await this.modalCtrl.create({
                component: DealerSelectorComponent,
                cssClass: 'app-modal-selector',
            });
    
            await modal.present();
    
            await modal.onDidDismiss();
    
            // Restore focus after modal dismissal
            const focusableElement = document.querySelector('ion-icon[name="menu-outline"]');
            if (focusableElement instanceof HTMLElement) {
                focusableElement.focus();
            }
        } catch (error) {
            console.error('Error with Dealer Selector Modal:', error);
        }
    }
    
    async onAlertClick() {
        try {
            if (this.userService.admin !== 1) {
                await this.nativeNotifications.clearAlertNotifications();
            }

            const modal = await this.modalCtrl.create({
                component: AlertComponent,
                cssClass: 'app-modal-alerts',
            });
    
            await modal.present();
    
            await modal.onDidDismiss();
    
            // Restore focus after modal dismissal
            const focusableElement = document.querySelector('ion-icon[name="notifications-outline"]');
            if (focusableElement instanceof HTMLElement) {
                focusableElement.focus();
            }
        } catch (error) {
            console.error('Error with Alert Modal:', error);
        }
    }

    onMarkAsRead(): void {
        this.getAuthenticatedUser();
    }

    private notifyNativeAlert(alertData: any): void {
        const message = typeof alertData === 'string' ? alertData : alertData?.message || '';
        const explicitType = typeof alertData === 'string' ? '' : (alertData?.type || '').toLowerCase();
        const normalized = (message || '').toLowerCase();
        const conversationId = typeof alertData === 'string'
            ? null
            : alertData?.conversation_id || alertData?.conversationId || alertData?.ConversationId || null;
        if (!normalized || normalized.includes('incoming call')) {
            return;
        }

        if (explicitType === 'email') {
            this.nativeNotifications.notifyAlert(message, 'email', conversationId);
            return;
        }

        if (explicitType === 'sms' || explicitType === 'message') {
            this.nativeNotifications.notifyAlert(message, 'sms', conversationId);
            return;
        }

        if (normalized.includes('new email')) {
            this.nativeNotifications.notifyAlert(message, 'email', conversationId);
            return;
        }

        if (normalized.includes('new text')) {
            this.nativeNotifications.notifyAlert(message, 'sms', conversationId);
            return;
        }

        this.nativeNotifications.notifyAlert(message, 'alert');
    }

    private notifyNativeConversation(conversationData: any): void {
        const message = conversationData?.message || conversationData?.body || '';
        const type = `${conversationData?.type || conversationData?.channel_type || ''}`.toLowerCase();
        const conversationId =
            conversationData?.ConversationId ||
            conversationData?.conversationId ||
            conversationData?.conversation_id ||
            null;

        if (type.includes('email')) {
            this.nativeNotifications.notifyAlert(message || 'New email received', 'email', conversationId);
            return;
        }

        if (type.includes('sms') || type.includes('text') || type.includes('message')) {
            this.nativeNotifications.notifyAlert(message || 'New text received', 'sms', conversationId);
            return;
        }

        this.notifyNativeAlert(message);
    }

    private notifyNativeleads(leadsData: any, total: string): void {
        const type = `${leadsData?.type || leadsData?.channel_type || ''}`.toLowerCase();
        if (!type || type.includes('lead')) {
            this.nativeNotifications.notifyAlert('New leads received: ' + total, 'leads');
            return;
        }
    }

    private hasAnyRole(roleIds: number[]): boolean {
        const selectedRoleIds = new Set(roleIds);

        return (this.userService.roles || []).some((role: any) => {
            if (typeof role === 'number') {
                return selectedRoleIds.has(role);
            }

            return selectedRoleIds.has(Number(role?.role_id || role?.id));
        });
    }
    
    

    /**
     * [onLogoutClick - Logs the current user out]
     * @returns {void}
     */
    onLogoutClick(): void {
        this.authService.logout();

        // Reset User
        this.userService.id = 0;
        this.show.profile = false;
    }

    onProfileToggle(event: Event): void {
        event.stopPropagation();
        this.show.profile = !this.show.profile;
    }

    onProfileClick(event: Event): void {
        event.stopPropagation();
    }

    onHide(): void{
        this.show.profile = false;
        this.contextMenu.deleteMessage();

    }
}
